"use client";

import { useLayoutEffect, useRef, useState, type FormEvent } from "react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import type { PlaceResult } from "@/lib/api/misc";
import { apiRequest } from "@/lib/api/_fetch";

interface PrashnaResult {
  id: string;
  prashna_lagna: string;
  ruling_planets: string[];
  mode: string;
  answer_direction: string;
  confidence_score: number;
  hora_lord: string;
  moon_rashi: string;
  moon_nakshatra: string;
  question_category: { name_en: string; emoji: string };
  prediction_summary: string;
  house_analysis: Array<{
    house_number: number;
    sign: string;
    significance: string;
    is_strong: boolean;
  }>;
}

interface Props {
  locale?: "en" | "hi";
}

const DIRECTION_LABEL: Record<string, { label: string; color: string }> = {
  positive: { label: "Favourable", color: "#22863a" },
  negative: { label: "Challenging", color: "#cb2431" },
  mixed: { label: "Mixed", color: "#b08800" },
};

const DIRECTION_LABEL_HI: Record<string, { label: string; color: string }> = {
  positive: { label: "अनुकूल", color: "#22863a" },
  negative: { label: "प्रतिकूल", color: "#cb2431" },
  mixed: { label: "मिश्रित", color: "#b08800" },
};

export default function PrashnaClient({ locale = "en" }: Props) {
  const isHi = locale === "hi";

  const t = prashnaLabels(isHi);

  // Keep the high-frequency question value out of React state. Cloudflare RUM
  // identified this textarea as the persistent mobile INP owner: putting every
  // keystroke in the page-root state rerendered the location autocomplete and
  // any prior result tree. The DOM owns the text; React only observes the
  // empty/non-empty transition needed by the submit affordance.
  const questionRef = useRef("");
  const [hasQuestion, setHasQuestion] = useState(false);
  const [locationText, setLocationText] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PrashnaResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Rule 58: the API response inserts a result taller than the form and pushes
  // the evergreen guide below it after the input-exclusion window. Anchor the
  // new region before paint so that growth happens below the viewport.
  useLayoutEffect(() => {
    if (!result || !resultRef.current) return;
    resultRef.current.scrollIntoView({ behavior: "auto", block: "start" });
  }, [result]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const question = questionRef.current.trim();
    if (question.length < 5) {
      setError(
        isHi ? "कृपया कम से कम 5 अक्षरों का प्रश्न लिखें।" : "Please enter at least 5 characters."
      );
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);

    try {
      const payload: Record<string, unknown> = {
        question,
        language: locale,
        question_time: new Date().toISOString(),
      };
      if (selectedPlace) {
        payload.latitude = selectedPlace.latitude;
        payload.longitude = selectedPlace.longitude;
        payload.timezone = selectedPlace.timezone;
        payload.question_location = selectedPlace.display ?? selectedPlace.name;
      }

      // apiRequest prepends API_BASE which already ends in /api/v1 — pass the
      // bare path, not /api/v1/prashna (that 404s as /api/v1/api/v1/prashna).
      const data = await apiRequest<PrashnaResult>("/prashna", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setResult(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isHi
            ? "कुछ गलत हुआ। पुनः प्रयास करें।"
            : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const dirLabels = isHi ? DIRECTION_LABEL_HI : DIRECTION_LABEL;

  return (
    <div>
      <PrashnaForm
        hasQuestion={hasQuestion}
        locationText={locationText}
        loading={loading}
        error={error}
        isHi={isHi}
        t={t}
        onQuestionChange={(value) => {
          questionRef.current = value;
          setHasQuestion(Boolean(value.trim()));
        }}
        onLocationChange={setLocationText}
        onPlaceSelect={(place) => {
          setSelectedPlace(place);
          setLocationText(place.display ?? place.name);
        }}
        onSubmit={handleSubmit}
      />
      {result && (
        <div ref={resultRef}>
          <PrashnaResultPanel result={result} dirLabels={dirLabels} t={t} />
        </div>
      )}
    </div>
  );
}

function prashnaLabels(isHi: boolean) {
  return {
    questionLabel: isHi ? "आपका प्रश्न" : "Your Question",
    questionPlaceholder: isHi
      ? "उदाहरण: क्या मुझे इस महीने नौकरी मिलेगी?"
      : "e.g. Will I get the job offer this month?",
    locationLabel: isHi ? "आपका वर्तमान स्थान" : "Your Current Location",
    locationPlaceholder: isHi ? "शहर खोजें..." : "Search city...",
    locationHint: isHi
      ? "प्रश्न का चार्ट आपके वर्तमान स्थान से बनता है, जन्म स्थान से नहीं।"
      : "Chart is cast for where you are right now, not your birth place.",
    submitLabel: isHi ? "प्रश्न कुंडली बनाएं" : "Cast Prashna Kundali",
    calculating: isHi ? "गणना हो रही है..." : "Casting chart…",
    lagnaLabel: isHi ? "प्रश्न लग्न" : "Prashna Lagna",
    horaLabel: isHi ? "होरा लॉर्ड" : "Hora Lord",
    significatorLabel: isHi ? "रूलिंग ग्रह" : "Ruling Planets",
    moonLabel: isHi ? "चंद्र राशि" : "Moon Rashi",
    confidenceLabel: isHi ? "विश्वास स्तर" : "Confidence",
    summaryLabel: isHi ? "व्याख्या" : "Interpretation",
    houseAnalysisLabel: isHi ? "भाव विश्लेषण" : "House Analysis",
  };
}

type PrashnaLabels = ReturnType<typeof prashnaLabels>;

function PrashnaForm({
  hasQuestion,
  locationText,
  loading,
  error,
  isHi,
  t,
  onQuestionChange,
  onLocationChange,
  onPlaceSelect,
  onSubmit,
}: {
  hasQuestion: boolean;
  locationText: string;
  loading: boolean;
  error: string | null;
  isHi: boolean;
  t: PrashnaLabels;
  onQuestionChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onPlaceSelect: (place: PlaceResult) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <label className="block text-[13px] font-semibold mb-1 text-ish-dark">
          {t.questionLabel}
        </label>
        <textarea
          id="prashna-question"
          name="question"
          defaultValue=""
          onChange={(event) => onQuestionChange(event.target.value)}
          placeholder={t.questionPlaceholder}
          rows={3}
          required
          data-cwv-interaction-target="prashna-question"
          className="w-full rounded-lg border px-3 py-2 text-[14px] focus:outline-none focus:ring-2 resize-none"
          style={{
            borderColor: "var(--ish-border)",
            color: "var(--ish-dark)",
            backgroundColor: "var(--ish-bg-card)",
          }}
        />
      </div>

      <div>
        <label className="block text-[13px] font-semibold mb-1 text-ish-dark">
          {t.locationLabel}{" "}
          <span className="font-normal text-ish-text-faint">
            ({isHi ? "वैकल्पिक" : "optional"})
          </span>
        </label>
        <PlaceAutocomplete
          value={locationText}
          onChange={onLocationChange}
          onSelect={onPlaceSelect}
          placeholder={t.locationPlaceholder}
        />
        <p className="text-[12px] mt-1 text-ish-text-faint">{t.locationHint}</p>
      </div>

      {error && (
        <p
          className="text-[13px] rounded-lg px-3 py-2"
          style={{
            color: "var(--color-error, #cb2431)",
            backgroundColor: "var(--color-error-bg, #fff0f0)",
          }}
        >
          {error}
        </p>
      )}

      <button
        id="prashna-submit"
        type="submit"
        disabled={loading || !hasQuestion}
        className="w-full rounded-lg py-3 text-[14px] font-semibold transition-opacity disabled:opacity-50"
        style={{ backgroundColor: "var(--ish-accent)", color: "#fff" }}
      >
        {loading ? t.calculating : t.submitLabel}
      </button>
    </form>
  );
}

function PrashnaResultPanel({
  result,
  dirLabels,
  t,
}: {
  result: PrashnaResult;
  dirLabels: Record<string, { label: string; color: string }>;
  t: PrashnaLabels;
}) {
  return (
    <div className="mt-8 space-y-5">
      <DirectionCard result={result} dirLabels={dirLabels} t={t} />
      <PredictionSummary result={result} t={t} />
      <HouseAnalysis result={result} t={t} />
    </div>
  );
}

function DirectionCard({
  result,
  dirLabels,
  t,
}: {
  result: PrashnaResult;
  dirLabels: Record<string, { label: string; color: string }>;
  t: PrashnaLabels;
}) {
  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: "var(--ish-accent-light)", border: "1px solid var(--ish-border)" }}
    >
      <p className="text-[12px] font-semibold uppercase tracking-wide mb-1 text-ish-text-faint">
        {result.question_category.emoji} {result.question_category.name_en}
      </p>
      <div className="flex items-center gap-3 mb-3">
        <span
          className="text-[20px] font-bold"
          style={{ color: dirLabels[result.answer_direction]?.color ?? "var(--ish-dark)" }}
        >
          {dirLabels[result.answer_direction]?.label ?? result.answer_direction}
        </span>
        <span className="text-[13px] text-ish-text-muted">
          {t.confidenceLabel}: {Math.round(result.confidence_score * 100)}%
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t.lagnaLabel, value: result.prashna_lagna },
          { label: t.horaLabel, value: result.hora_lord },
          { label: t.moonLabel, value: result.moon_rashi },
          { label: t.significatorLabel, value: result.ruling_planets.join(", ") },
        ].map(({ label, value }) => (
          <ResultFact key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  );
}

function ResultFact({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-lg p-3"
      style={{ backgroundColor: "var(--ish-bg-card)", border: "1px solid var(--ish-border)" }}
    >
      <p className="text-[11px] uppercase tracking-wide text-ish-text-faint">{label}</p>
      <p className="text-[14px] font-semibold capitalize mt-1 text-ish-dark">{value}</p>
    </div>
  );
}

function PredictionSummary({ result, t }: { result: PrashnaResult; t: PrashnaLabels }) {
  if (!result.prediction_summary) return null;
  return (
    <div className="card-sacred p-5">
      <p className="text-[13px] font-semibold mb-2 text-ish-accent">{t.summaryLabel}</p>
      <p className="text-[14px] leading-relaxed text-ish-dark" style={{ whiteSpace: "pre-wrap" }}>
        {result.prediction_summary}
      </p>
    </div>
  );
}

function HouseAnalysis({ result, t }: { result: PrashnaResult; t: PrashnaLabels }) {
  if (result.house_analysis.length === 0) return null;
  return (
    <div>
      <p className="text-[13px] font-semibold mb-3 text-ish-dark">{t.houseAnalysisLabel}</p>
      <div className="space-y-2">
        {result.house_analysis.map((house) => (
          <HouseAnalysisRow key={house.house_number} house={house} />
        ))}
      </div>
    </div>
  );
}

function HouseAnalysisRow({ house }: { house: PrashnaResult["house_analysis"][number] }) {
  return (
    <div
      className="rounded-lg p-3 flex items-start gap-3"
      style={{ backgroundColor: "var(--ish-bg-card)", border: "1px solid var(--ish-border)" }}
    >
      <span
        className="text-[12px] font-bold rounded-full w-7 h-7 flex items-center justify-center shrink-0"
        style={{
          backgroundColor: house.is_strong ? "var(--ish-accent)" : "var(--ish-border)",
          color: house.is_strong ? "#fff" : "var(--ish-text-muted)",
        }}
      >
        {house.house_number}
      </span>
      <div>
        <p className="text-[13px] font-semibold capitalize text-ish-dark">{house.sign}</p>
        <p className="text-[12px] text-ish-text-muted">{house.significance}</p>
      </div>
    </div>
  );
}
